/**
 * Feed Prioritization & Randomization Utility
 *
 * Rules:
 * 1. Page size is 10 items per batch.
 * 2. If current user follows creator B, B's posts/videos MUST be prioritized
 *    and guaranteed to reach the follower's feed batch.
 * 3. Non-followed posts in the batch are randomized for fresh discovery.
 */

export function prioritizeAndRandomizeFeed<T>(
  items: T[],
  followingUids: Set<string>,
  getOwnerUid: (item: T) => string | undefined,
  pageSize = 10
): T[] {
  if (!items || items.length === 0) return [];

  const followedItems: T[] = [];
  const otherItems: T[] = [];

  for (const item of items) {
    const ownerUid = getOwnerUid(item);
    if (ownerUid && followingUids.has(ownerUid)) {
      followedItems.push(item);
    } else {
      otherItems.push(item);
    }
  }

  // Shuffle other items for randomized feed discovery
  for (let i = otherItems.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [otherItems[i], otherItems[j]] = [otherItems[j], otherItems[i]];
  }

  // Combine followed items first, then randomized other items
  const combined = [...followedItems, ...otherItems];

  // Deduplicate and return up to pageSize
  return combined.slice(0, pageSize);
}
